import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Scale, ArrowLeft, Sparkles, Send, Share2, RotateCcw, Check, X, AlertTriangle, FileText, Download, GitCompare, Coins, Lock, Plus, Trash2, Upload, Stamp } from "lucide-react";
import { ClauseWizard } from "@/components/ClauseWizard";
import { buildPacketHtml, openPacketPdf } from "@/lib/packet";

const TABS = ["Questionnaire", "Legal Issues", "Draft Agreement", "Will It Hold Up?", "State Law", "Disclosure (Exhibit A)", "Signing", "Change History", "Share", "Activity", "AI Assistant"];
const STATES = ["California", "Texas", "New York", "Florida", "Illinois", "Massachusetts", "Other"];
const NAME_TO_CODE = { California: "CA", Texas: "TX", "New York": "NY", Florida: "FL", Illinois: "IL", Massachusetts: "MA" };
const STATUSES = ["Draft", "Shared With Partner", "Under Review", "Attorney Review", "Changes Requested", "Final Review", "Ready for Execution"];

const Q_SECTIONS = [
  { title: "Personal & Family", fields: [
    { k: "state_residence", label: "State of residence", type: "select", opts: STATES },
    { k: "state_marriage", label: "State where marriage will occur", type: "text" },
    { k: "citizenship", label: "Citizenship", type: "text" },
    { k: "country_origin", label: "Country of origin", type: "text" },
    { k: "marital_status", label: "Current marital status", type: "select", opts: ["Never married", "Divorced", "Widowed"] },
    { k: "previous_marriages", label: "Previously married?", type: "yesno" },
    { k: "children_previous", label: "Children from previous relationships?", type: "yesno" },
    { k: "children_together", label: "Children together?", type: "yesno" },
    { k: "future_children", label: "Plan for future children?", type: "yesno" },
  ]},
  { title: "Relationship", fields: [
    { k: "marriage_date", label: "Date of (anticipated) marriage", type: "text" },
    { k: "relationship_length", label: "Length of relationship", type: "text" },
    { k: "existing_agreements", label: "Existing marital agreements?", type: "yesno" },
    { k: "obligations_former", label: "Obligations to a former spouse?", type: "yesno" },
  ]},
  { title: "Party 1, Employment & Income", fields: [
    { k: "employer", label: "Employer", type: "text" },
    { k: "self_employed", label: "Self-employed / business owner?", type: "yesno" },
    { k: "annual_income", label: "Annual income", type: "text" },
    { k: "owns_business", label: "Own a business or professional practice?", type: "yesno" },
    { k: "has_equity", label: "Equity interests / stock / options?", type: "yesno" },
    { k: "has_ip", label: "Intellectual property?", type: "yesno" },
  ]},
  { title: "Party 1, Assets", fields: [
    { k: "owns_real_estate", label: "Own real estate / land?", type: "yesno" },
    { k: "has_retirement", label: "Retirement accounts / pensions?", type: "yesno" },
    { k: "has_investments", label: "Investments / savings?", type: "yesno" },
    { k: "has_crypto", label: "Cryptocurrency?", type: "yesno" },
    { k: "has_trusts", label: "Trusts?", type: "yesno" },
    { k: "has_inheritance", label: "Inheritances or expected inheritance?", type: "yesno" },
    { k: "foreign_assets", label: "Assets in another country?", type: "yesno" },
    { k: "separate_property", label: "Separate property to protect?", type: "text" },
  ]},
  { title: "Party 1, Debts", fields: [
    { k: "has_debts", label: "Any debts (mortgage, loans, cards)?", type: "yesno" },
    { k: "student_loans", label: "Student loans?", type: "yesno" },
    { k: "business_debt", label: "Business debt?", type: "yesno" },
  ]},
  { title: "Party 2, Employment, Assets & Debts", fields: [
    { k: "p2_employer", label: "Party 2 employer", type: "text" },
    { k: "p2_self_employed", label: "Party 2 self-employed / business owner?", type: "yesno" },
    { k: "p2_annual_income", label: "Party 2 annual income", type: "text" },
    { k: "p2_owns_business", label: "Party 2 owns a business / practice?", type: "yesno" },
    { k: "p2_owns_real_estate", label: "Party 2 owns real estate / land?", type: "yesno" },
    { k: "p2_has_retirement", label: "Party 2 retirement accounts / pensions?", type: "yesno" },
    { k: "p2_has_investments", label: "Party 2 investments / savings?", type: "yesno" },
    { k: "p2_has_inheritance", label: "Party 2 inheritances or expected inheritance?", type: "yesno" },
    { k: "p2_separate_property", label: "Party 2 separate property to protect?", type: "text" },
    { k: "p2_has_debts", label: "Party 2 has debts?", type: "yesno" },
  ]},
  { title: "Future Property", fields: [
    { k: "future_income", label: "How to treat future income?", type: "text" },
    { k: "future_business", label: "How to treat future businesses?", type: "text" },
    { k: "appreciation", label: "How to treat appreciation of separate property?", type: "text" },
  ]},
  { title: "Faith / Values (drafting preferences only)", fields: [
    { k: "religious", label: "Religious or faith-based considerations", type: "text" },
    { k: "cultural", label: "Cultural or family considerations", type: "text" },
  ]},
  { title: "Clauses you're considering (optional)", clauses: true, fields: [
    { k: "clause_spousal_support", label: "Waive or limit spousal support?", type: "yesno", clause: "spousal_support" },
    { k: "clause_infidelity", label: "Add an infidelity ('cheating') clause?", type: "yesno", clause: "infidelity" },
    { k: "clause_lifestyle", label: "Add lifestyle / personal-conduct terms?", type: "yesno", clause: "lifestyle_conduct" },
    { k: "clause_social_image", label: "Add a social-image (social media) clause?", type: "yesno", clause: "social_image" },
    { k: "clause_death", label: "Add a death / estate clause?", type: "yesno", clause: "death" },
    { k: "clause_confidentiality", label: "Add a confidentiality clause?", type: "yesno", clause: "confidentiality" },
    { k: "clause_child_custody", label: "Try to address child custody?", type: "yesno", clause: "child_custody" },
    { k: "clause_child_support", label: "Try to address child support?", type: "yesno", clause: "child_support" },
  ]},
];

export default function Workspace() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [matter, setMatter] = useState(null);
  const [tab, setTab] = useState("Questionnaire");
  const [answers, setAnswers] = useState({});

  const load = () => api.get(`/matters/${id}`).then((r) => { setMatter(r.data); setAnswers(r.data.answers || {}); });
  useEffect(() => { if (user === false) nav("/login"); }, [user, nav]);
  useEffect(() => { if (user) load().catch(() => { toast.error("No access"); nav("/dashboard"); }); }, [id, user]);

  if (!matter) return <div className="min-h-screen flex items-center justify-center text-[#5C6B73]">Loading…</div>;

  return (
    <div className="min-h-screen bg-white flex">
      <aside className="w-60 border-r border-[#E2E8F0] p-4 hidden md:flex flex-col shrink-0">
        <Link to="/dashboard" className="flex items-center gap-2 text-sm text-[#5C6B73] hover:text-[#0B132B] mb-6"><ArrowLeft className="w-4 h-4" />Dashboard</Link>
        <div className="font-display font-semibold text-[#0B132B] leading-tight mb-1">{matter.title}</div>
        <StatusBadge matter={matter} onChange={load} />
        <nav className="mt-6 space-y-1">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.replace(/[^a-zA-Z]/g, "")}`} className={`w-full text-left text-sm px-3 py-2 rounded-lg ${tab === t ? "bg-[#0B132B] text-white" : "text-[#5C6B73] hover:bg-[#F0F2F5]"}`}>{t}</button>
          ))}
        </nav>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="md:hidden border-b border-[#E2E8F0] p-3 flex gap-2 overflow-x-auto">
          {TABS.map((t) => <button key={t} onClick={() => setTab(t)} className={`text-xs whitespace-nowrap px-3 py-1.5 rounded-full ${tab === t ? "bg-[#0B132B] text-white" : "bg-[#F0F2F5] text-[#5C6B73]"}`}>{t}</button>)}
        </div>
        <div className="p-6 md:p-8 max-w-4xl">
          {user?.subscription_status === "past_due" && (
            <div className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="readonly-banner">
              <Lock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-amber-800">Your subscription payment is past due</div>
                <div className="text-amber-700 mt-0.5">Your documents are read-only for a short grace period. Update your billing to keep editing before access is locked.</div>
              </div>
              <Link to="/billing" data-testid="readonly-fix-billing" className="whitespace-nowrap bg-amber-600 text-white text-sm px-4 py-2 rounded-full font-medium hover:bg-amber-700 transition-colors">Fix billing</Link>
            </div>
          )}
          {tab === "Questionnaire" && <Questionnaire id={id} answers={answers} setAnswers={setAnswers} reload={load} docType={matter?.doc_type} />}
          {tab === "Legal Issues" && <LegalIssues id={id} onGoto={setTab} />}
          {tab === "Draft Agreement" && <DraftTab id={id} matter={matter} reload={load} />}
          {tab === "Will It Hold Up?" && <EnforceabilityTab id={id} matter={matter} onGoto={setTab} />}
          {tab === "State Law" && <StateLawTab state={matter.state} docType={matter.doc_type} />}
          {tab === "Disclosure (Exhibit A)" && <DisclosureTab id={id} matter={matter} />}
          {tab === "Signing" && <SigningTab id={id} matter={matter} reload={load} />}
          {tab === "Change History" && <VersionsTab id={id} reload={load} />}
          {tab === "Share" && <ShareTab id={id} />}
          {tab === "Activity" && <ActivityTab id={id} />}
          {tab === "AI Assistant" && <AITab id={id} />}
        </div>
      </main>
    </div>
  );
}

function StatusBadge({ matter, onChange }) {
  const set = async (s) => { await api.put(`/matters/${matter.id}/status`, { status: s }); onChange(); };
  return (
    <select value={matter.status} onChange={(e) => set(e.target.value)} data-testid="status-select" className="text-xs bg-[#F0F2F5] border border-[#E2E8F0] rounded-full px-3 py-1 text-[#0B132B]">
      {STATUSES.map((s) => <option key={s}>{s}</option>)}
    </select>
  );
}

const inp = "w-full bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm text-[#0B132B] focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]";

const CLAUSE_SEV = {
  high: "border-red-200 bg-red-50 text-red-800",
  medium: "border-amber-200 bg-amber-50 text-amber-800",
  info: "border-blue-200 bg-blue-50 text-blue-800",
};

function Questionnaire({ id, answers, setAnswers, reload, docType }) {
  const [saving, setSaving] = useState(false);
  const [clauseFlags, setClauseFlags] = useState({});
  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const stateCode = NAME_TO_CODE[answers.state_residence] || "";

  const checkClause = async (clause) => {
    try {
      const { data } = await api.post("/legal/clause-check", { state: stateCode, doc_type: docType || "prenuptial", clauses: [clause] });
      setClauseFlags((f) => ({ ...f, [clause]: data.flags }));
    } catch (e) { /* advisory only */ }
  };

  // Re-run checks for any clause already answered "Yes" (on load and when state changes)
  useEffect(() => {
    Q_SECTIONS.flatMap((s) => s.fields).filter((f) => f.clause && answers[f.k] === "Yes").forEach((f) => checkClause(f.clause));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateCode]);

  const onYesNo = (f, o) => {
    set(f.k, o);
    if (f.clause) {
      if (o === "Yes") checkClause(f.clause);
      else setClauseFlags((fl) => ({ ...fl, [f.clause]: [] }));
    }
  };

  const highCount = Object.values(clauseFlags).flat().filter((x) => x && x.severity === "high").length;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/matters/${id}/questionnaire`, { answers });
      if (data.affected_sections?.length) toast.info(`This change may affect: ${data.affected_sections.join(", ")}`, { duration: 6000 });
      else toast.success("Saved");
      reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-[#0B132B] mb-1">Guided questionnaire</h1>
      <p className="text-sm text-[#5C6B73] mb-4">Your answers drive the draft. Faith/values answers are treated as drafting preferences, not law.</p>
      {highCount > 0 && (
        <div className="mb-6 flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-4" data-testid="clause-warning-banner">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">{highCount} clause{highCount > 1 ? "s" : ""} you selected may be unenforceable or work against you in {answers.state_residence || "your state"}. Review the flags below and consider having an attorney check them. <a href="/learn/clauses" className="underline">Learn more</a></div>
        </div>
      )}
      {(() => {
        const allF = Q_SECTIONS.flatMap((s) => s.fields);
        const total = allF.length;
        const filled = allF.filter((f) => { const v = answers[f.k]; return v !== undefined && v !== null && String(v).trim() !== ""; }).length;
        const pct = Math.round((filled / total) * 100);
        return (
          <div className="mb-6 sticky top-0 bg-white py-2 z-10" data-testid="questionnaire-progress">
            <div className="flex justify-between text-xs text-[#5C6B73] mb-1"><span>{filled} of {total} answered</span><span className="font-medium text-[#0B132B]" data-testid="progress-pct">{pct}% complete</span></div>
            <div className="h-2 bg-[#F0F2F5] rounded-full overflow-hidden"><div className="h-full bg-[#4A6FA5] transition-all duration-300" style={{ width: pct + "%" }} /></div>
          </div>
        );
      })()}
      <div className="space-y-8">
        {Q_SECTIONS.map((sec) => (
          <div key={sec.title}>
            <h2 className="text-base font-semibold text-[#0B132B] border-b border-[#E2E8F0] pb-2 mb-4">{sec.title}</h2>
            {sec.clauses && <p className="text-xs text-[#5C6B73] -mt-2 mb-4">Toggle a clause to instantly see whether it may be risky or unenforceable in your state.</p>}
            <div className="grid sm:grid-cols-2 gap-4">
              {sec.fields.map((f) => (
                <div key={f.k}>
                  <label className="block text-xs text-[#5C6B73] mb-1">{f.label}</label>
                  {f.type === "text" && <input className={inp} data-testid={`q-${f.k}`} value={answers[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} />}
                  {f.type === "select" && <select className={inp} data-testid={`q-${f.k}`} value={answers[f.k] || ""} onChange={(e) => set(f.k, e.target.value)}><option value="">Select…</option>{f.opts.map((o) => <option key={o}>{o}</option>)}</select>}
                  {f.type === "yesno" && (
                    <div className="flex gap-2">
                      {["Yes", "No"].map((o) => <button key={o} type="button" onClick={() => onYesNo(f, o)} data-testid={`q-${f.k}-${o}`} className={`flex-1 text-sm border rounded-lg py-2 ${answers[f.k] === o ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-[#E2E8F0] text-[#0B132B]"}`}>{o}</button>)}
                    </div>
                  )}
                  {f.clause && answers[f.k] === "Yes" && (clauseFlags[f.clause] || []).length > 0 && (
                    <div className="mt-2 space-y-1" data-testid={`clause-flag-${f.clause}`}>
                      {clauseFlags[f.clause].map((fl) => (
                        <div key={fl.id} className={`text-xs rounded-lg px-3 py-2 border ${CLAUSE_SEV[fl.severity] || CLAUSE_SEV.info}`}>
                          <strong>{fl.title}.</strong> {fl.message} <span className="opacity-70">Source: {fl.citation}.</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-[#5C6B73] mt-4">Clause flags are advisory legal information, not legal advice. Always confirm with a licensed attorney in your state.</p>
      <button onClick={save} disabled={saving} data-testid="save-questionnaire" className="mt-4 bg-[#0B132B] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#1C2541] transition-colors">{saving ? "Saving…" : "Save answers"}</button>
    </div>
  );
}

function LegalIssues({ id, onGoto }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/matters/${id}/legal-issues`).then((r) => setData(r.data)); }, [id]);
  if (!data) return <p className="text-[#5C6B73]">Loading…</p>;
  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-[#0B132B] mb-1">Legal issues</h1>
      <p className="text-sm text-[#5C6B73] mb-6">Issue spotting based on your answers and {data.state || "your"} law. {data.rule_count} state rules loaded.</p>
      <div className="space-y-4">
        {data.issues.length === 0 && <div className="border border-dashed border-[#E2E8F0] rounded-xl p-8 text-[#5C6B73]">No issues flagged yet. Complete more of the questionnaire.</div>}
        {data.issues.map((iss) => (
          <div key={iss.id} className="border border-[#E2E8F0] rounded-xl p-5" data-testid={`issue-${iss.id}`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-[#0B132B]"><strong>What you told us:</strong> {iss.told}.</div>
                <div className="text-sm text-[#5C6B73] mt-1"><strong className="text-[#0B132B]">Consider:</strong> how the agreement addresses {iss.considerations.join(", ")}.</div>
                <div className="text-xs text-[#4A6FA5] mt-1">Affected section: {iss.affected_section}</div>
                {iss.rules?.map((r) => (
                  <div key={r.id} className="mt-2 bg-[#F0F2F5] rounded-lg p-3 text-xs text-[#5C6B73]"><strong className="text-[#0B132B]">{r.category}:</strong> {r.rule}<div className="mt-1">Source: {r.source} · Reviewed {r.last_reviewed}</div></div>
                ))}
                {iss.missing && <div className="mt-2 text-xs text-orange-600">Still missing: {iss.missing}</div>}
                <button onClick={() => onGoto("Draft Agreement")} className="mt-3 text-sm text-[#4A6FA5] font-medium">Review this section →</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DraftTab({ id, matter, reload }) {
  const [version, setVersion] = useState(null);
  const [gen, setGen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const loadV = () => matter.current_version > 0 ? api.get(`/matters/${id}/versions/${matter.current_version}`).then((r) => setVersion(r.data)) : setVersion(null);
  useEffect(() => { loadV(); api.get(`/matters/${id}/suggestions`).then((r) => setSuggestions(r.data.filter((s) => s.status === "pending"))); }, [id, matter.current_version]);
  const generate = async () => {
    setGen(true);
    try { await api.post(`/matters/${id}/generate`, {}); toast.success("Draft generated"); reload(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setGen(false); }
  };
  const decide = async (sid, d) => { await api.post(`/matters/${id}/suggestions/${sid}/${d}`); toast.success(`Suggestion ${d}ed`); reload(); api.get(`/matters/${id}/suggestions`).then((r) => setSuggestions(r.data.filter((s) => s.status === "pending"))); };
  const [costs, setCosts] = useState([]);
  const [balance, setBalance] = useState(0);
  const [ledger, setLedger] = useState([]);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadCredits = () => { api.get("/credit-costs").then((r) => setCosts(r.data.actions)); api.get("/credits/ledger").then((r) => { setBalance(r.data.credits); setLedger(r.data.ledger); }); };
  useEffect(() => { loadCredits(); }, [id]);
  const [disc, setDisc] = useState(null);
  useEffect(() => { api.get(`/matters/${id}/disclosure`).then((r) => setDisc(r.data)).catch(() => {}); }, [id]);
  const askQuote = async (actionId) => { try { const { data } = await api.post(`/matters/${id}/customize`, { action_id: actionId, confirm: false }); setQuote(data.quote); } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } };
  const confirmCustomize = async () => { if (!quote) return; setBusy(true); try { const { data } = await api.post(`/matters/${id}/customize`, { action_id: quote.action.id, confirm: true }); toast.success(`Applied ${quote.action.label} (${data.credits_charged} credit${data.credits_charged > 1 ? "s" : ""})`); setQuote(null); reload(); loadCredits(); } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); } };
  const exportPdf = () => {
    if (!version) return;
    if (!openPacketPdf(buildPacketHtml({ matter, version, disc, signatures: matter.signatures }))) toast.error("Please allow pop-ups to export the PDF");
  };
  const exportDocx = async () => {
    try {
      const res = await fetch(`${API}/matters/${id}/export/docx`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `${matter.party1_name}_${matter.party2_name}_${matter.doc_type}.docx`.replace(/\s+/g, "_");
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Generate a draft first, then export"); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold text-[#0B132B]">Draft agreement {version && <span className="text-sm font-normal text-[#5C6B73]">· v{version.version}</span>}</h1>
        <button onClick={generate} disabled={gen} data-testid="generate-draft-btn" className="inline-flex items-center gap-2 bg-[#0B132B] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#1C2541] transition-colors"><Sparkles className="w-4 h-4" />{gen ? "Generating…" : version ? "Regenerate" : "Generate draft"}</button>
      </div>
      {suggestions.length > 0 && (
        <div className="mb-6 space-y-2">
          {suggestions.map((s) => (
            <div key={s.id} className="border border-orange-200 bg-orange-50 rounded-lg p-4" data-testid={`suggestion-${s.id}`}>
              <div className="text-xs font-semibold text-orange-700 mb-1">Suggested change · {s.section_id} · by {s.author_name}</div>
              <div className="text-xs text-[#5C6B73] line-through">{s.original}</div>
              <div className="text-sm text-[#0B132B]">{s.suggested}</div>
              <div className="mt-2 flex gap-2">
                <button onClick={() => decide(s.id, "accept")} className="text-xs bg-green-600 text-white px-3 py-1 rounded-full flex items-center gap-1"><Check className="w-3 h-3" />Accept</button>
                <button onClick={() => decide(s.id, "reject")} className="text-xs bg-white border border-[#E2E8F0] px-3 py-1 rounded-full flex items-center gap-1"><X className="w-3 h-3" />Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!version ? (
        <div className="border border-dashed border-[#E2E8F0] rounded-xl p-12 text-center text-[#5C6B73]">No draft yet. Complete the questionnaire, then generate.</div>
      ) : (
        <div className="border border-[#E2E8F0] rounded-xl p-8 bg-white grain">
          <div className="text-xs text-[#5C6B73] mb-6 border border-[#E2E8F0] rounded p-2">Not legal advice. This is a draft for review, have separate counsel review before signing.</div>
          {version.sections.map((s) => (
            <section key={s.id} className="mb-6" data-testid={`section-${s.id}`}>
              <h3 className="font-semibold text-[#0B132B] mb-1">{s.heading}</h3>
              <p className="text-sm text-[#0B132B] leading-relaxed whitespace-pre-wrap">{s.body}</p>
            </section>
          ))}
          <div className="mt-4 flex gap-3">
            <button onClick={exportPdf} data-testid="export-pdf-btn" className="inline-flex items-center gap-2 text-sm text-[#4A6FA5] font-medium"><Download className="w-4 h-4" />Export PDF</button>
            <button onClick={exportDocx} data-testid="export-docx-btn" className="inline-flex items-center gap-2 text-sm text-[#4A6FA5] font-medium"><FileText className="w-4 h-4" />Export Word (.docx)</button>
          </div>
        </div>
      )}
      <div className="mt-8 border border-[#E2E8F0] rounded-xl p-6" data-testid="customize-panel">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <h2 className="text-lg font-semibold text-[#0B132B]">Request a customized update</h2>
          <span className="inline-flex items-center gap-1.5 text-sm text-[#5C6B73]" data-testid="credit-balance-draft"><Coins className="w-4 h-4 text-[#4A6FA5]" />{balance} credit{balance === 1 ? "" : "s"} available</span>
        </div>
        <p className="text-sm text-[#5C6B73] mb-4">Substantial custom drafting uses credits. You will always see the exact cost and your balance before anything is charged. Your subscription already covers unlimited workspaces and standard revisions.</p>
        {balance <= 2 && (
          <div className="mb-4 flex items-center justify-between gap-3 bg-[#F0F4FA] border border-[#4A6FA5]/30 rounded-lg px-4 py-3" data-testid="low-credits-nudge">
            <div className="flex items-center gap-2 text-sm text-[#0B132B]">
              <Coins className="w-4 h-4 text-[#4A6FA5] shrink-0" />
              <span>{balance === 0 ? "You're out of credits." : `You're running low on credits (${balance} left).`} Top up so your next customization is ready to go.</span>
            </div>
            <Link to="/billing" data-testid="low-credits-topup" className="whitespace-nowrap bg-[#0B132B] text-white text-sm px-4 py-2 rounded-full font-medium hover:bg-[#1C2541] transition-colors">Top up</Link>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {costs.map((a) => (
            <button key={a.id} onClick={() => askQuote(a.id)} data-testid={`customize-${a.id}`} className="text-left border border-[#E2E8F0] rounded-lg p-4 hover:border-[#4A6FA5] transition-colors">
              <div className="flex items-center justify-between"><span className="font-medium text-[#0B132B]">{a.label}</span><span className="text-xs bg-[#0B132B] text-white rounded-full px-2 py-0.5">{a.credits} credit{a.credits > 1 ? "s" : ""}</span></div>
              <p className="text-xs text-[#5C6B73] mt-1">{a.description}</p>
            </button>
          ))}
        </div>
        {ledger.filter((l) => l.type === "usage").length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-[#0B132B] mb-2">Recent credit usage</h3>
            <div className="space-y-1">
              {ledger.filter((l) => l.type === "usage").slice(0, 6).map((l) => (
                <div key={l.id} className="flex justify-between text-xs text-[#5C6B73] border-b border-[#F0F2F5] py-1"><span>{l.action}{l.matter_title ? ` · ${l.matter_title}` : ""}</span><span>{Math.abs(l.credits)} credit(s) · {new Date(l.date).toLocaleDateString()}</span></div>
              ))}
            </div>
          </div>
        )}
      </div>
      {quote && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-5" onClick={() => !busy && setQuote(null)} data-testid="quote-modal">
          <div className="bg-white rounded-2xl p-7 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl font-bold text-[#0B132B]">Confirm customized update</h3>
            <p className="text-sm text-[#5C6B73] mt-1">{quote.action.label}: {quote.action.description}</p>
            <div className="mt-4 bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg p-4 text-sm">
              <div className="flex justify-between py-1"><span className="text-[#5C6B73]">Estimated cost</span><span className="font-medium text-[#0B132B]" data-testid="quote-cost">{quote.credits} credit{quote.credits > 1 ? "s" : ""}</span></div>
              <div className="flex justify-between py-1"><span className="text-[#5C6B73]">Your balance</span><span className="text-[#0B132B]">{quote.balance}</span></div>
              <div className="flex justify-between py-1 border-t border-[#E2E8F0] mt-1 pt-2"><span className="text-[#5C6B73]">Balance after</span><span className={`font-medium ${quote.sufficient ? "text-[#0B132B]" : "text-red-600"}`}>{quote.balance_after}</span></div>
            </div>
            {quote.sufficient ? (
              <div className="mt-5 flex gap-3">
                <button onClick={confirmCustomize} disabled={busy} data-testid="confirm-customize" className="flex-1 bg-[#0B132B] text-white py-2.5 rounded-full text-sm font-medium">{busy ? "Applying…" : `Use ${quote.credits} credit${quote.credits > 1 ? "s" : ""} & apply`}</button>
                <button onClick={() => setQuote(null)} disabled={busy} className="px-4 rounded-full border border-[#E2E8F0] text-sm">Cancel</button>
              </div>
            ) : (
              <div className="mt-5">
                <p className="text-sm text-red-600 mb-3">You do not have enough credits for this update.</p>
                <a href="/billing" className="block text-center bg-[#0B132B] text-white py-2.5 rounded-full text-sm font-medium" data-testid="buy-credits-link">Buy more credits</a>
                <button onClick={() => setQuote(null)} className="w-full mt-2 text-sm text-[#5C6B73]">Cancel</button>
              </div>
            )}
            <p className="mt-4 text-[11px] text-[#94a3b8]">Nothing is charged until you confirm. Credits are non-refundable once an update is generated.</p>
          </div>
        </div>
      )}
      <div className="mt-8"><ClauseWizard defaultDocType={matter?.doc_type || "prenuptial"} /></div>
    </div>
  );
}

function StateLawTab({ state, docType }) {
  const [rules, setRules] = useState([]);
  useEffect(() => { if (state) api.get(`/rules?state=${encodeURIComponent(state)}&doc_type=${docType}`).then((r) => setRules(r.data)); }, [state, docType]);
  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-[#0B132B] mb-1">State law, {state || "not selected"}</h1>
      <p className="text-sm text-[#5C6B73] mb-6">Sourced, dated rules. We flag confidence and never invent rules.</p>
      {!state && <div className="border border-dashed border-[#E2E8F0] rounded-xl p-8 text-[#5C6B73]">Select a state of residence in the questionnaire.</div>}
      <div className="space-y-3">
        {rules.map((r) => (
          <div key={r.id} className="border border-[#E2E8F0] rounded-xl p-5">
            <div className="flex justify-between gap-2 flex-wrap"><span className="text-xs font-semibold uppercase text-[#4A6FA5]">{r.category}</span><span className={`text-xs px-2 py-0.5 rounded-full ${r.confidence === "high" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{r.confidence}</span></div>
            <p className="mt-2 text-sm text-[#0B132B]">{r.rule}</p>
            <div className="mt-2 text-xs text-[#5C6B73]">Source: {r.source} · Reviewed {r.last_reviewed} · v{r.version}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DisclosureTab({ id, matter }) {
  const blank = { income: "", separate_property: "", assets: [], debts: [] };
  const [sched, setSched] = useState({ p1: { ...blank }, p2: { ...blank } });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/matters/${id}/disclosure`).then((r) => {
    const s = r.data.schedule || {};
    setSched({ p1: { ...blank, ...(s.p1 || {}) }, p2: { ...blank, ...(s.p2 || {}) } });
    setFiles(r.data.files || []);
  });
  useEffect(() => { load(); }, [id]);
  const upd = (pk, patch) => setSched((p) => ({ ...p, [pk]: { ...p[pk], ...patch } }));
  const addRow = (pk, kind) => upd(pk, { [kind]: [...sched[pk][kind], { desc: "", value: "" }] });
  const setRow = (pk, kind, i, field, v) => upd(pk, { [kind]: sched[pk][kind].map((r, idx) => idx === i ? { ...r, [field]: v } : r) });
  const delRow = (pk, kind, i) => upd(pk, { [kind]: sched[pk][kind].filter((_, idx) => idx !== i) });
  const save = async () => { setSaving(true); try { await api.put(`/matters/${id}/disclosure`, { schedule: sched }); toast.success("Disclosure saved — attaches as Exhibit A on export"); } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setSaving(false); } };
  const upload = async (party, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("party", party);
    try { await api.post(`/matters/${id}/disclosure/files`, fd); toast.success("Uploaded"); load(); }
    catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Upload failed"); }
    e.target.value = "";
  };
  const download = async (fid, nm) => { try { const res = await api.get(`/matters/${id}/disclosure/files/${fid}/download`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = nm; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 5000); } catch { toast.error("Could not open file"); } };
  const removeFile = async (fid) => { try { await api.delete(`/matters/${id}/disclosure/files/${fid}`); load(); } catch { toast.error("Delete failed"); } };

  const Party = ({ pk, party, name }) => {
    const d = sched[pk]; const pfiles = files.filter((f) => f.party === party);
    const items = [["income", !!String(d.income || "").trim()], ["assets or debts", (d.assets.length + d.debts.length) > 0], ["a supporting document", pfiles.length > 0]];
    const pct = Math.round(items.filter((i) => i[1]).length / items.length * 100);
    const missing = items.filter((i) => !i[1]).map((i) => i[0]);
    return (
      <div className="border border-[#E2E8F0] rounded-xl p-5" data-testid={`disclosure-${party}`}>
        <div className="flex items-center justify-between border-b border-[#E2E8F0] pb-2 mb-4"><h2 className="font-semibold text-[#0B132B]">{name || party}</h2><span className="text-xs text-[#5C6B73]">{party}</span></div>
        <div className="mb-4">
          <div className="flex justify-between text-xs mb-1"><span className="text-[#5C6B73]">Disclosure completeness</span><span className="font-medium text-[#0B132B]">{pct}%</span></div>
          <div className="h-2 bg-[#E2E8F0] rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" data-testid={`disc-${pk}-bar`} style={{ width: `${pct}%`, background: pct === 100 ? "#16a34a" : pct >= 50 ? "#d97706" : "#dc2626" }} /></div>
          {pct < 100 && <p className="text-xs text-amber-700 mt-1" data-testid={`disc-${pk}-nudge`}>{name || party} still needs: {missing.join(", ")}.</p>}
        </div>
        <label className="block text-xs text-[#5C6B73] mb-1">Annual income</label>
        <input className={inp + " mb-3"} data-testid={`disc-${pk}-income`} value={d.income} onChange={(e) => upd(pk, { income: e.target.value })} />
        {["assets", "debts"].map((kind) => (
          <div key={kind} className="mb-4">
            <div className="flex items-center justify-between mb-1"><span className="text-xs font-semibold uppercase text-[#4A6FA5]">{kind}</span><button type="button" onClick={() => addRow(pk, kind)} data-testid={`disc-${pk}-add-${kind}`} className="text-xs text-[#4A6FA5] inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button></div>
            {d[kind].length === 0 && <p className="text-xs text-[#5C6B73]">None listed.</p>}
            {d[kind].map((r, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input className={inp} placeholder={kind === "assets" ? "e.g. Condo in Austin" : "e.g. Student loan"} value={r.desc} onChange={(e) => setRow(pk, kind, i, "desc", e.target.value)} />
                <input className={inp + " w-32"} placeholder="Value $" value={r.value} onChange={(e) => setRow(pk, kind, i, "value", e.target.value)} />
                <button type="button" onClick={() => delRow(pk, kind, i)} className="text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        ))}
        <label className="block text-xs text-[#5C6B73] mb-1">Separate property to protect (notes)</label>
        <input className={inp + " mb-4"} value={d.separate_property} onChange={(e) => upd(pk, { separate_property: e.target.value })} />
        <div className="border-t border-[#E2E8F0] pt-3">
          <div className="flex items-center justify-between mb-2"><span className="text-xs font-semibold uppercase text-[#4A6FA5]">Supporting documents</span>
            <label className="text-xs text-[#4A6FA5] inline-flex items-center gap-1 cursor-pointer"><Upload className="w-3 h-3" /> Upload<input type="file" className="hidden" data-testid={`disc-${pk}-upload`} onChange={(e) => upload(party, e)} /></label>
          </div>
          {pfiles.length === 0 && <p className="text-xs text-[#5C6B73]">No files uploaded.</p>}
          {pfiles.map((f) => (
            <div key={f.id} className="flex items-center justify-between text-sm border border-[#E2E8F0] rounded-lg px-3 py-2 mb-1" data-testid={`disc-file-${f.id}`}>
              <button onClick={() => download(f.id, f.original_filename)} className="text-[#0B132B] truncate text-left hover:text-[#4A6FA5] flex items-center gap-2"><FileText className="w-4 h-4 shrink-0" /><span className="truncate">{f.original_filename}</span></button>
              <button onClick={() => removeFile(f.id)} className="text-red-500 shrink-0 ml-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="disclosure-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h1 className="font-display text-2xl font-bold text-[#0B132B]">Financial disclosure — Exhibit A</h1>
        <button onClick={save} disabled={saving} data-testid="disc-save" className="bg-[#0B132B] text-white px-5 py-2 rounded-full text-sm font-medium">{saving ? "Saving…" : "Save disclosure"}</button>
      </div>
      <p className="text-sm text-[#5C6B73] mb-6">Full, fair disclosure is the #1 reason agreements hold up. List each party's assets and debts and attach statements — this schedule auto-attaches as <strong>Exhibit A</strong> when you export.</p>
      <div className="grid md:grid-cols-2 gap-5">
        <Party pk="p1" party="Party 1" name={matter.party1_name} />
        <Party pk="p2" party="Party 2" name={matter.party2_name} />
      </div>
    </div>
  );
}

function wordDiff(a, b) {
  const A = (a || "").split(/(\s+)/), B = (b || "").split(/(\s+)/);
  const n = A.length, m = B.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out = []; let i = 0, j = 0;
  while (i < n && j < m) { if (A[i] === B[j]) { out.push({ t: "same", w: A[i] }); i++; j++; } else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: "del", w: A[i] }); i++; } else { out.push({ t: "add", w: B[j] }); j++; } }
  while (i < n) out.push({ t: "del", w: A[i++] });
  while (j < m) out.push({ t: "add", w: B[j++] });
  return out;
}
function DiffText({ diff, side }) {
  return <p className="text-sm leading-relaxed whitespace-pre-wrap">{diff.map((d, i) => {
    if (d.t === "same") return <span key={i}>{d.w}</span>;
    if (d.t === "del" && side === "old") return <span key={i} className="bg-red-100 text-red-700 line-through">{d.w}</span>;
    if (d.t === "add" && side === "new") return <span key={i} className="bg-green-100 text-green-700">{d.w}</span>;
    return null;
  })}</p>;
}

function VersionsTab({ id, reload }) {
  const [versions, setVersions] = useState([]);
  const [compare, setCompare] = useState(false);
  const [aVer, setAVer] = useState(null);
  const [bVer, setBVer] = useState(null);
  const load = () => api.get(`/matters/${id}/versions`).then((r) => { setVersions(r.data); if (r.data.length >= 2) { setAVer(r.data[1].version); setBVer(r.data[0].version); } else if (r.data.length === 1) { setAVer(r.data[0].version); setBVer(r.data[0].version); } });
  useEffect(() => { load(); }, [id]);
  const restore = async (v) => { await api.post(`/matters/${id}/versions/${v}/restore`); toast.success("Restored as new version"); load(); reload(); };
  const A = versions.find((v) => v.version === aVer);
  const B = versions.find((v) => v.version === bVer);
  const sectionIds = A && B ? Array.from(new Set([...A.sections.map((s) => s.id), ...B.sections.map((s) => s.id)])) : [];
  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold text-[#0B132B]">Change history</h1>
        {versions.length >= 2 && <button onClick={() => setCompare(!compare)} data-testid="toggle-compare" className="inline-flex items-center gap-2 text-sm border border-[#0B132B]/20 rounded-full px-4 py-2 hover:border-[#0B132B] transition-colors"><GitCompare className="w-4 h-4" />{compare ? "Hide comparison" : "Compare versions"}</button>}
      </div>
      {compare && A && B && (
        <div className="mb-8 border border-[#E2E8F0] rounded-xl p-5" data-testid="redline-compare">
          <div className="flex items-center gap-3 mb-4 text-sm flex-wrap">
            <select className={inp + " w-auto"} value={aVer} onChange={(e) => setAVer(+e.target.value)} data-testid="compare-a">{versions.map((v) => <option key={v.version} value={v.version}>v{v.version}, {v.label}</option>)}</select>
            <span className="text-[#5C6B73]">vs</span>
            <select className={inp + " w-auto"} value={bVer} onChange={(e) => setBVer(+e.target.value)} data-testid="compare-b">{versions.map((v) => <option key={v.version} value={v.version}>v{v.version}, {v.label}</option>)}</select>
            <span className="ml-auto text-xs text-[#5C6B73]"><span className="bg-red-100 text-red-700 px-1 line-through">removed</span> · <span className="bg-green-100 text-green-700 px-1">added</span></span>
          </div>
          <div className="space-y-5">
            {sectionIds.map((sid) => {
              const oa = A.sections.find((s) => s.id === sid);
              const ob = B.sections.find((s) => s.id === sid);
              const diff = wordDiff(oa?.body || "", ob?.body || "");
              const changed = diff.some((d) => d.t !== "same");
              return (
                <div key={sid} data-testid={`diff-section-${sid}`}>
                  <div className="flex items-center gap-2 mb-1"><h3 className="font-semibold text-[#0B132B] text-sm">{ob?.heading || oa?.heading || sid}</h3>{!changed && <span className="text-xs text-[#5C6B73]">unchanged</span>}{!oa && <span className="text-xs text-green-700">new section</span>}{!ob && <span className="text-xs text-red-700">removed section</span>}</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg p-3"><div className="text-xs text-[#5C6B73] mb-1">v{aVer}</div><DiffText diff={diff} side="old" /></div>
                    <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg p-3"><div className="text-xs text-[#5C6B73] mb-1">v{bVer}</div><DiffText diff={diff} side="new" /></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {versions.length === 0 && <div className="border border-dashed border-[#E2E8F0] rounded-xl p-8 text-[#5C6B73]">No versions yet.</div>}
      <div className="space-y-3">
        {versions.map((v) => (
          <div key={v.version} className="flex items-center justify-between border border-[#E2E8F0] rounded-xl p-4" data-testid={`version-${v.version}`}>
            <div><div className="font-medium text-[#0B132B]">Version {v.version}, {v.label}</div><div className="text-xs text-[#5C6B73]">{new Date(v.created_at).toLocaleString()} · by {v.created_by} · {v.sections.length} sections</div></div>
            <button onClick={() => restore(v.version)} className="inline-flex items-center gap-1 text-xs text-[#4A6FA5] font-medium"><RotateCcw className="w-3.5 h-3.5" />Restore</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShareTab({ id }) {
  const [shares, setShares] = useState([]);
  const [form, setForm] = useState({ role: "attorney", permission: "view", email: "", expires_days: 7, show_financials: false, show_questionnaire: false });
  const load = () => api.get(`/matters/${id}/shares`).then((r) => setShares(r.data));
  useEffect(() => { load(); }, [id]);
  const create = async () => {
    try { const { data } = await api.post(`/matters/${id}/shares`, { ...form, origin_url: window.location.origin }); const url = `${window.location.origin}/review/${data.token}`; try { await navigator.clipboard?.writeText(url); } catch (e) { console.debug("Clipboard copy blocked:", e); } toast.success(form.email ? "Link created, copied & emailed" : "Link created & copied", { description: url }); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const revoke = async (sid) => { await api.post(`/shares/${sid}/revoke`); toast.success("Revoked"); load(); };
  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-[#0B132B] mb-1">Share agreement</h1>
      <p className="text-sm text-[#5C6B73] mb-6">Invite your partner or create a secure, revocable review link for an attorney or professional.</p>
      <div className="border border-[#E2E8F0] rounded-xl p-5 space-y-4 mb-6">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B73]">Recipient</label>
            <select className={inp} data-testid="share-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}><option value="partner">Partner</option><option value="attorney">Attorney</option><option value="professional">Other professional</option></select></div>
          <div><label className="text-xs text-[#5C6B73]">Permission</label>
            <select className={inp} data-testid="share-permission" value={form.permission} onChange={(e) => setForm({ ...form, permission: e.target.value })}><option value="view">View only</option><option value="comment">Comment</option><option value="suggest">Suggest changes</option><option value="edit">Edit</option></select></div>
          <div><label className="text-xs text-[#5C6B73]">Email (optional)</label><input className={inp} data-testid="share-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><label className="text-xs text-[#5C6B73]">Link expires (days)</label><input type="number" className={inp} data-testid="share-expires" value={form.expires_days} onChange={(e) => setForm({ ...form, expires_days: +e.target.value })} /></div>
        </div>
        <div className="flex gap-6 text-sm text-[#0B132B]">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.show_financials} onChange={(e) => setForm({ ...form, show_financials: e.target.checked })} />Show financial schedules</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.show_questionnaire} onChange={(e) => setForm({ ...form, show_questionnaire: e.target.checked })} />Show questionnaire</label>
        </div>
        <button onClick={create} data-testid="create-share-btn" className="inline-flex items-center gap-2 bg-[#0B132B] text-white px-5 py-2.5 rounded-full text-sm font-medium"><Share2 className="w-4 h-4" />Create secure link</button>
      </div>
      <h2 className="font-semibold text-[#0B132B] mb-3">Active links</h2>
      <div className="space-y-2">
        {shares.length === 0 && <p className="text-sm text-[#5C6B73]">No active links.</p>}
        {shares.map((s) => (
          <div key={s.id} className="flex items-center justify-between border border-[#E2E8F0] rounded-lg p-4" data-testid={`share-${s.id}`}>
            <div><div className="text-sm font-medium text-[#0B132B] capitalize">{s.role} · {s.permission} access</div><div className="text-xs text-[#5C6B73]">Expires {new Date(s.expires_at).toLocaleDateString()} · /review/{s.token.slice(0, 8)}…</div></div>
            <button onClick={() => revoke(s.id)} data-testid={`revoke-${s.id}`} className="text-xs text-red-600 font-medium">Revoke</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityTab({ id }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/matters/${id}/activity`).then((r) => setItems(r.data)); }, [id]);
  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-[#0B132B] mb-1">Activity & audit trail</h1>
      <p className="text-sm text-[#5C6B73] mb-6">Read-only history of every action on this agreement.</p>
      <div className="space-y-2">
        {items.map((a) => (
          <div key={a.id} className="flex gap-3 text-sm border-l-2 border-[#E2E8F0] pl-4 py-1" data-testid={`activity-${a.id}`}>
            <span className="text-[#5C6B73] w-40 shrink-0">{new Date(a.created_at).toLocaleString()}</span>
            <span className="text-[#0B132B]"><strong>{a.actor}</strong> {a.action}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AITab({ id }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => { api.get(`/matters/${id}/ai/history`).then((r) => setMsgs((cur) => cur.length ? cur : r.data)); }, [id]);
  useEffect(() => { boxRef.current?.scrollTo(0, boxRef.current.scrollHeight); }, [msgs]);
  const suggestions = ["What happens to my business if we divorce?", "How should we address my inheritance?", "What about my retirement account?", "We have property in another country, what should we consider?"];
  const send = async (q) => {
    const question = q || text; if (!question.trim() || streaming) return;
    setText(""); setMsgs((m) => [...m, { role: "user", text: question }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/matters/${id}/ai/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: question }) });
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      while (true) {
        const { done, value } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) { if (!p.startsWith("data: ")) continue; const d = JSON.parse(p.slice(6)); if (d.delta) setMsgs((m) => { const c = [...m]; c[c.length - 1].text += d.delta; return c; }); }
      }
    } catch { toast.error("AI error"); } finally { setStreaming(false); }
  };
  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      <h1 className="font-display text-2xl font-bold text-[#0B132B] mb-1">AI assistant</h1>
      <p className="text-sm text-[#5C6B73] mb-4">Legal information & drafting considerations, not legal advice. Answers use your answers, state, and rules.</p>
      <div ref={boxRef} className="flex-1 overflow-y-auto space-y-4 pr-2">
        {msgs.length === 0 && (
          <div className="grid sm:grid-cols-2 gap-2">
            {suggestions.map((s) => <button key={s} onClick={() => send(s)} className="text-left text-sm border border-[#E2E8F0] rounded-lg p-3 text-[#0B132B] hover:border-[#4A6FA5] transition-colors">{s}</button>)}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div data-testid={`ai-msg-${m.role}`} className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#0B132B] text-white" : "bg-[#F0F2F5] text-[#0B132B]"}`}>{m.text || "…"}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <input className={inp} data-testid="ai-input" placeholder="Ask about your agreement…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
        <button onClick={() => send()} disabled={streaming} data-testid="ai-send" className="bg-[#0B132B] text-white px-4 rounded-lg"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
}


const ENF_PILL = { strong: "bg-green-100 text-green-800", attention: "bg-amber-100 text-amber-800", at_risk: "bg-red-100 text-red-700" };
const ENF_LABEL = { strong: "Strong", attention: "Needs attention", at_risk: "At risk" };
const scoreColor = (s) => (s >= 80 ? "#16a34a" : s >= 55 ? "#d97706" : "#dc2626");

function EnforceabilityTab({ id, matter, onGoto }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/matters/${id}/enforceability`).then((r) => setData(r.data)).catch(() => {}); }, [id, matter.current_version]);
  const save = async (patch) => {
    setSaving(true);
    try { const { data } = await api.put(`/matters/${id}/enforceability`, patch); setData(data); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!data) return <div className="text-[#5C6B73]">Loading…</div>;
  const inputs = data.inputs || {};
  const yn = (k, label) => (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-sm text-[#0B132B]">{label}</span>
      <div className="flex gap-2">
        {["Yes", "No"].map((o) => (
          <button key={o} type="button" disabled={saving} data-testid={`enf-${k}-${o}`} onClick={() => save({ [k]: o })}
            className={`text-sm border rounded-lg px-4 py-1.5 ${inputs[k] === o ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-[#E2E8F0] text-[#0B132B] bg-white"}`}>{o}</button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="max-w-3xl" data-testid="enforceability-tab">
      <h2 className="font-display text-2xl font-bold text-[#0B132B]">Will it hold up?</h2>
      <p className="text-sm text-[#5C6B73] mt-1 mb-6">A plain-language readiness estimate for {data.state || "your state"}. Not legal advice.</p>

      <div className="flex items-center gap-6 border border-[#E2E8F0] rounded-2xl p-6 mb-6 bg-[#F8F9FA]">
        <div className="relative w-28 h-28 shrink-0" data-testid="enf-score-ring" style={{ background: `conic-gradient(${scoreColor(data.score)} ${data.score * 3.6}deg, #E2E8F0 0deg)`, borderRadius: "9999px" }}>
          <div className="absolute inset-2 bg-white rounded-full flex flex-col items-center justify-center">
            <span className="font-display text-3xl font-black" style={{ color: scoreColor(data.score) }}>{data.score}</span>
            <span className="text-[10px] uppercase tracking-wide text-[#5C6B73]">/ 100</span>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-[#5C6B73]">Enforceability readiness</div>
          <div className="font-display text-2xl font-bold" style={{ color: scoreColor(data.score) }} data-testid="enf-label">{data.label}</div>
          <p className="text-sm text-[#5C6B73] mt-1">Improve the checklist below to raise your score.</p>
        </div>
      </div>

      <div className="space-y-3 mb-8">
        {data.dimensions.map((d) => (
          <div key={d.key} className="border border-[#E2E8F0] rounded-xl p-4" data-testid={`enf-dim-${d.key}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-[#0B132B]">{d.key}</span>
              <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${ENF_PILL[d.status]}`}>{ENF_LABEL[d.status]}</span>
            </div>
            <p className="text-sm text-[#2A3441] mt-1">{d.message}</p>
            {d.action && <p className="text-xs text-[#4A6FA5] mt-1">→ {d.action}</p>}
          </div>
        ))}
      </div>

      <div className="border border-[#E2E8F0] rounded-xl p-5 mb-6">
        <h3 className="font-semibold text-[#0B132B] mb-2">A few quick questions sharpen your score</h3>
        {yn("enf_counsel_p1", "Will you (Party 1) have your own attorney?")}
        {yn("enf_counsel_p2", "Will your partner (Party 2) have their own attorney?")}
        {yn("enf_partner_reviewed", "Has your partner reviewed the full agreement?")}
        <div className="flex items-center justify-between gap-3 py-2">
          <span className="text-sm text-[#0B132B]">Days before the wedding you plan to sign</span>
          <input type="number" min="0" data-testid="enf-days" defaultValue={inputs.enf_days_before_wedding || ""} onBlur={(e) => save({ enf_days_before_wedding: e.target.value })}
            className="w-28 bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg px-3 py-1.5 text-sm text-right" />
        </div>
      </div>

      {data.clause_flags.length > 0 && (
        <div className="border border-[#E2E8F0] rounded-xl p-5 mb-6">
          <h3 className="font-semibold text-[#0B132B] mb-3">Clauses that need attention</h3>
          <div className="space-y-2">
            {data.clause_flags.map((f) => (
              <div key={f.id} className="text-sm" data-testid={`enf-flag-${f.id}`}>
                <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full mr-2 ${f.severity === "high" ? "bg-red-100 text-red-700" : f.severity === "medium" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>{f.severity}</span>
                <span className="text-[#0B132B] font-medium">{f.title}</span>
                <p className="text-[#5C6B73] mt-0.5">{f.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-[#5C6B73]">{data.disclaimer}</p>
    </div>
  );
}

function SigningTab({ id, matter, reload }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [party, setParty] = useState("Party 1");
  const [typed, setTyped] = useState("");
  const [version, setVersion] = useState(null);
  const [disc, setDisc] = useState(null);
  useEffect(() => {
    if (matter.current_version) api.get(`/matters/${id}/versions/${matter.current_version}`).then((r) => setVersion(r.data)).catch(() => {});
    api.get(`/matters/${id}/disclosure`).then((r) => setDisc(r.data)).catch(() => {});
  }, [id, matter.current_version]);
  const sigs = matter.signatures || {};
  const posOf = (e) => { const r = canvasRef.current.getBoundingClientRect(); const t = e.touches?.[0]; return [(t ? t.clientX : e.clientX) - r.left, (t ? t.clientY : e.clientY) - r.top]; };
  const start = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const [x, y] = posOf(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const [x, y] = posOf(e); ctx.lineTo(x, y); ctx.strokeStyle = "#0B132B"; ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.stroke(); hasInk.current = true; };
  const end = () => { drawing.current = false; };
  const clearPad = () => { const c = canvasRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height); hasInk.current = false; };
  const sign = async () => {
    let image = ""; let name = typed.trim();
    if (hasInk.current && canvasRef.current) image = canvasRef.current.toDataURL("image/png");
    if (!name && !image) { toast.error("Type your name or draw your signature"); return; }
    if (!name) name = party === "Party 1" ? matter.party1_name : matter.party2_name;
    try { await api.post(`/matters/${id}/sign`, { party, name, image, typed: !image }); toast.success(`${party} signed`); clearPad(); setTyped(""); reload(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const clearSig = async (pk) => { try { await api.delete(`/matters/${id}/sign/${pk}`); reload(); } catch { toast.error("Could not clear"); } };
  const downloadPacket = () => { if (!version) { toast.error("Generate a draft first"); return; } if (!openPacketPdf(buildPacketHtml({ matter, version, disc, signatures: matter.signatures }))) toast.error("Please allow pop-ups to download the packet"); };

  const SigStatus = ({ pk, party, name }) => {
    const s = sigs[pk];
    return (
      <div className="border border-[#E2E8F0] rounded-xl p-4" data-testid={`sig-status-${pk}`}>
        <div className="flex items-center justify-between"><span className="font-semibold text-[#0B132B]">{name || party}</span><span className="text-xs text-[#5C6B73]">{party}</span></div>
        {s ? (
          <div className="mt-2">
            {s.image ? <img src={s.image} alt="signature" className="h-12" /> : <span style={{ fontFamily: "'Snell Roundhand','Brush Script MT',cursive" }} className="text-2xl text-[#0B132B]">{s.name}</span>}
            <div className="text-xs text-[#5C6B73] mt-1">Signed {(s.signed_at || "").slice(0, 10)}{s.ip ? ` · IP ${s.ip}` : ""}</div>
            <button onClick={() => clearSig(pk)} data-testid={`sig-clear-${pk}`} className="text-xs text-red-500 mt-1">Clear signature</button>
          </div>
        ) : <div className="text-sm text-[#5C6B73] mt-2">Not signed yet.</div>}
      </div>
    );
  };

  return (
    <div className="max-w-3xl" data-testid="signing-tab">
      <h1 className="font-display text-2xl font-bold text-[#0B132B] mb-1">Sign &amp; execute</h1>
      <p className="text-sm text-[#5C6B73] mb-6">Capture each party's signature, then download a signing packet with a notary block. Electronic signatures are for convenience — some states require notarization or witnesses.</p>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <SigStatus pk="p1" party="Party 1" name={matter.party1_name} />
        <SigStatus pk="p2" party="Party 2" name={matter.party2_name} />
      </div>

      <div className="border border-[#E2E8F0] rounded-xl p-5 mb-6">
        <div className="flex gap-2 mb-4">
          {["Party 1", "Party 2"].map((p) => <button key={p} onClick={() => setParty(p)} data-testid={`sign-party-${p.replace(/[^a-zA-Z0-9]/g, "")}`} className={`text-sm border rounded-lg px-4 py-1.5 ${party === p ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-[#E2E8F0] text-[#0B132B]"}`}>{p === "Party 1" ? (matter.party1_name || p) : (matter.party2_name || p)}</button>)}
        </div>
        <label className="block text-xs text-[#5C6B73] mb-1">Type full legal name (adopts a typed signature)</label>
        <input className={inp + " mb-3"} data-testid="sign-typed" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="e.g. Alexandra J. Owner" />
        <label className="block text-xs text-[#5C6B73] mb-1">…or draw your signature</label>
        <canvas ref={canvasRef} width={560} height={140} data-testid="sign-canvas"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          className="w-full border border-dashed border-[#E2E8F0] rounded-lg bg-[#F8F9FA] touch-none" style={{ height: 140 }} />
        <div className="flex items-center gap-3 mt-3">
          <button onClick={sign} data-testid="sign-submit" className="bg-[#0B132B] text-white px-5 py-2 rounded-full text-sm font-medium">Adopt &amp; sign as {party}</button>
          <button onClick={clearPad} className="text-sm text-[#5C6B73]">Clear pad</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button onClick={downloadPacket} data-testid="download-packet" className="inline-flex items-center gap-2 bg-[#0B132B] text-white px-5 py-2.5 rounded-full text-sm font-medium"><Download className="w-4 h-4" /> Download signing packet (PDF)</button>
      </div>

      <div className="border border-[#E2E8F0] rounded-xl p-5 bg-[#F8F9FA]">
        <h3 className="font-semibold text-[#0B132B] flex items-center gap-2"><Stamp className="w-4 h-4 text-[#4A6FA5]" /> Need it notarized?</h3>
        <p className="text-sm text-[#5C6B73] mt-1">Many prenups and postnups are stronger (and in some states required) when notarized. You can complete remote online notarization through a third-party service.</p>
        <a href="https://www.notarize.com" target="_blank" rel="noopener noreferrer" data-testid="notary-link" className="mt-3 inline-flex items-center gap-2 border border-[#0B132B] text-[#0B132B] px-4 py-2 rounded-full text-sm font-medium">Notarize online →</a>
        <p className="text-[11px] text-[#5C6B73] mt-3">NuPDocs is not affiliated with Notarize.com or Proof and does not provide notary services.</p>
      </div>
    </div>
  );
}
